import type { SyntheticEvent } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getFormInputProps } from "util/forms";
import { selectECommerceShoppingPlatforms } from "features/general/selectors";
import { UrlTextField } from "ui/atoms/TextField";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import StatefulSelector from "ui/widgets/StatefulSelector";
import OnboardingForm from "ui/forms/ECommerce/Onboarding/OnboardingForm";
import { OnboardingInputControl } from "ui/molecules/Input";
import { FormField, type OnboardingBusinessInformationProps } from "./types";

const BusinessInformation = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingBusinessInformationProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
                gutterBottom
            >
                Company Details
            </Typography>
            <OnboardingInputControl
                name={FormField.CompanyName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CompanyName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.CompanyName}
                        value={values[FormField.CompanyName]}
                        label='Company Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.RegistrationNumber}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.RegistrationNumber, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.RegistrationNumber}
                        value={values[FormField.RegistrationNumber]}
                        label='Registration Number'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CountryOfIncorporation}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.CountryOfIncorporation] ?? ''}
                        onChange={onAutocompleteChange(FormField.CountryOfIncorporation)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CountryOfIncorporation, form),
                            required: true,
                            name: FormField.CountryOfIncorporation,
                            label: 'Country of Incorporation'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.YearEstablished}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            label='Year of Establishment'
                            views={['year']}
                            disabled={disabled}
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.YearEstablished, form),
                                    name: FormField.YearEstablished,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={values[FormField.YearEstablished]}
                            onChange={value => {
                                setFieldValue(FormField.YearEstablished, value);
                                setTimeout(() => setFieldTouched(FormField.YearEstablished, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingInputControl
                name={FormField.WebsiteUrl}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <UrlTextField
                        {...getFormInputProps(FormField.WebsiteUrl, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.WebsiteUrl}
                        value={values[FormField.WebsiteUrl]}
                        label='Website URL'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.PrimaryLocation}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.PrimaryLocation, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.PrimaryLocation}
                        value={values[FormField.PrimaryLocation]}
                        label='Primary Location'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CountriesOfBusinessActivity}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        multiple
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.CountriesOfBusinessActivity]}
                        onChange={onAutocompleteChange(FormField.CountriesOfBusinessActivity)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CountriesOfBusinessActivity, form),
                            name: FormField.CountriesOfBusinessActivity,
                            label: 'Countries of Business Activity *'
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Which E-Commerce Platforms are you currently using?
            </Typography>
            <OnboardingInputControl
                name={FormField.Platforms}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <StatefulSelector
                        multiple
                        fullWidth
                        disablePortal
                        disabled={disabled}
                        size='small'
                        layout={null}
                        loading={isBootstrapDataLoading}
                        selectOptions={selectECommerceShoppingPlatforms}
                        value={values[FormField.Platforms]}
                        onChange={onAutocompleteChange(FormField.Platforms)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Platforms, form),
                            label: 'E-commerce Platforms in Use',
                            name: FormField.Platforms
                        }}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default BusinessInformation;
